import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGlobalState } from '../../utils/GlobalState';
import { ArrowLeft } from "lucide-react";

const rootCauseOptions = {
  "OC Issue": [
    "Polarizer Material (Hygroscopic Stress)",
    "OC defect",
    "OC gum unevenness",
    "Polarizer Gall",
    "OC COF Damage (Scratching)",
    "OC COF Damage (assembling)",
    "OC Broken (Gap Issue)"
  ],
  "LB Issue": [
    "Led too close to reflect sheet",
    "LB Warping",
    "LB LED Damage",
    "Lens Light shape poor",
    "LB Cable or Connector poor contact",
    "LED too close to the four sides",
    "Y Pitch too far",
    "LED far away from corner",
    "X Pitch too far"
  ],
  "Film Issue": [
    "Film gall (hardness related)",
    "Film space not enough",
    "Film edge or film hook tripping",
    "Film hygroscopic deformation",
    "Film is pressed too tight",
    "Film Issue (haze related)"
  ],
  "Diffuser Plate": [
    "DP issue (haze related)",
    "DP gall (hardness related)",
    "DP is gored by support pin"
  ],
  "Reflect Sheet": [
    "Reflect sheet punching or printing issue",
    "Reflect sheet wrapping",
    "Reflect sheet roulette or folding angle issue",
    "Reflect sheet size NG",
    "Reflect sheet collapsing"
  ],
  "Back Cover": [
    "Back cover flatness poor",
    "Back cover deformation",
    "Back cover size NG"
  ],
  "Front Cover": [
    "Front cover deformation"
  ],
  "Middle Cover": [
    "Middle Cover deformation"
  ],
  "Deco": [
    "Deco deformation"
  ],
  "Spacer": [
    "Spacer hardness issue"
  ],
  "Others": [
    "User input"
  ]
};

const IssueRootCauseDetail = () => {
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [customIssue, setCustomIssue] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { state: globalState, updateState } = useGlobalState();
  const {
    selectedCauses = ["Not Selected"],
    muraType: selectedMuraType = "Not Selected",
    yoloAnnotations = [],
    uploadedImage,
    recordedEntries,
    previouslySelectedTypes,
    selectedMuraTypes,
    issueType,
  } = location.state || {};

  useEffect(() => {
    if (!globalState || !globalState.suggestion || !globalState.predictions) {
      console.warn("Global state missing required fields, updating with incoming data");
      updateState({
        uploadedImage: uploadedImage || globalState?.uploadedImage || null,
        recordedEntries: recordedEntries || globalState?.recordedEntries || [],
        selectedMuraType: selectedMuraType || globalState?.selectedMuraType || "",
        yoloAnnotations: yoloAnnotations || globalState?.yoloAnnotations || [],
        issueType: issueType || globalState?.issueType || "MuraLabeling",
      });
    }
  }, [
    location.state,
    selectedCauses,
    selectedMuraType,
    yoloAnnotations,
  
    uploadedImage,
    recordedEntries,
    previouslySelectedTypes,
    selectedMuraTypes,
    issueType,
    globalState,
    updateState,
  ]);

  const getIssueDetails = () => {
    if (selectedCauses.length === 1 && rootCauseOptions[selectedCauses[0]]) {
      return rootCauseOptions[selectedCauses[0]];
    }
    return [];
  };

  const issueDetails = getIssueDetails();

  const handleIssueSelect = (issue) => {
    if (issue === "Others") {
      if (selectedIssues.includes("Others")) {
        setSelectedIssues([]);
        setCustomIssue("");
      } else {
        setSelectedIssues(["Others"]);
      }
    } else {
      if (selectedIssues.includes(issue)) {
        setSelectedIssues(selectedIssues.filter((i) => i !== issue));
      } else {
        setSelectedIssues([...selectedIssues.filter(i => i !== "Others"), issue]);
        setCustomIssue("");
      }
    }
  };

  const handleCustomIssueChange = (e) => {
    const value = e.target.value;
    setCustomIssue(value);
  };

  const handleApply = () => {
    let issuesToPass = [];
    if (selectedIssues.includes("Others") && customIssue.trim()) {
      issuesToPass = [customIssue];
    } else if (!selectedIssues.includes("Others")) {
      issuesToPass = [...selectedIssues];
    }

    if (issuesToPass.length === 0) {
      alert("Please select at least one issue detail or specify a custom one.");
      return;
    }

    updateState({
      uploadedImage: uploadedImage || globalState?.uploadedImage || null,
      recordedEntries: recordedEntries || globalState?.recordedEntries || [],
      selectedMuraType: selectedMuraType,
      yoloAnnotations,
      issueType: selectedMuraType.replace(/_/g, ' ').toLowerCase() === "pressing light leakage" ? "LightLeakage" : "MuraLabeling",
    });

    navigate("/Re-labeling", {
      state: {
        selectedCauses,
        selectedIssues: issuesToPass,
        muraType: selectedMuraType,
        uploadedImage,
        recordedEntries,
        previouslySelectedTypes,
        selectedMuraTypes,
        issueType: selectedMuraType.replace(/_/g, ' ').toLowerCase() === "pressing light leakage" ? "LightLeakage" : "MuraLabeling",
        yoloAnnotations,
        fromRootCauseDetail: true,
      },
    });
  };

  return (
    <div className="p-3 bg-black text-white min-h-screen font-sans">
      <div className="flex justify-between items-center mb-5">
        <ArrowLeft className="w-7 h-5 cursor-pointer left-0" onClick={() => navigate(-1)} />
        <h2 className="text-xl flex-1 text-center font-bold"> Root Cause Detail</h2>
      </div>

      <div className="mb-3">
        <label className="block text-sm sm:text-lg mt-3 mb-2 font-bold"> Root Cause Main Type:</label>
        <div className="w-full p-1 sm:text-lg rounded-lg bg-[#6B7280] border border-[#6B7280] text-white text-sm h-8 flex items-center justify-center">
          {selectedCauses.length > 0 ? selectedCauses.join(", ") : "Not Selected"}
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm sm:text-lg mt-3 mb-2 font-bold">Issue Detail:</label>
        <div className="space-y-2 md:space-y-4">
          {issueDetails.map((issue) => (
            <div
              key={issue}
              className={`flex justify-between items-center p-3 rounded-lg border ${
                selectedIssues.includes(issue) ? "border-blue-500" : "border-[#6B7280]"
              }`}
              onClick={() => handleIssueSelect(issue)}
            >
              <label htmlFor={issue} className="text-xs md:text-sm whitespace-nowrap md:whitespace-normal">
                {issue}
              </label>
              <div className="relative w-6 h-6">
                <input
                  type="checkbox"
                  id={issue}
                  name="issueDetail"
                  value={issue}
                  checked={selectedIssues.includes(issue)}
                  onChange={() => handleIssueSelect(issue)}
                  className="w-6 h-6 appearance-none border-2 border-[#6B7280] rounded-full cursor-pointer
                             checked:border-blue-500 checked:bg-transparent"
                />
                {selectedIssues.includes(issue) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          <div
            className={`flex justify-between items-center p-3 rounded-lg border ${
              selectedIssues.includes("Others") ? "border-blue-500" : "border-[#6B7280]"
            }`}
            onClick={() => handleIssueSelect("Others")}
          >
            <label htmlFor="Others" className="text-xs md:text-sm whitespace-nowrap md:whitespace-normal">
              Others
            </label>
            <div className="relative w-6 h-6">
              <input
                type="checkbox"
                id="Others"
                name="issueDetail"
                value="Others"
                checked={selectedIssues.includes("Others")}
                onChange={() => handleIssueSelect("Others")}
                className="w-6 h-6 appearance-none border-2 border-[#6B7280] rounded-full cursor-pointer
                           checked:border-blue-500 checked:bg-transparent"
              />
              {selectedIssues.includes("Others") && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedIssues.includes("Others") && (
          <div className="mt-2">
            <input
              type="text"
              value={customIssue}
              onChange={handleCustomIssueChange}
              placeholder="Please specify"
              className="w-full p-2 rounded-lg border-none bg-[#1E2A44] text-white text-sm box-border"
            />
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6 w-full">
        <button
          onClick={() => navigate(-1)}
          className="bg-[#1E2A44] text-white py-2 px-4 w-15 sm:w-44 sm:py-2 sm:px-4 md:w-64 lg:w-120 xl:w-[41rem] rounded-lg cursor-pointer text-xs sm:text-sm"
        >
          Back
        </button>
       
        <button
          onClick={handleApply}
          disabled={
            selectedIssues.length === 0 || 
            (selectedIssues.includes("Others") && customIssue.trim() === "")
          }
          className={`py-2 px-4 w-15 sm:w-44 sm:py-2 sm:px-4 md:w-64 lg:w-120 xl:w-[41rem] rounded-lg text-xs sm:text-sm
            ${
              (selectedIssues.length > 0 && !selectedIssues.includes("Others")) ||
              (selectedIssues.includes("Others") && customIssue.trim())
                ? "bg-blue-600 text-white cursor-pointer"
                : "bg-gray-400 text-gray-200 cursor-not-allowed"
            }
          `}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default IssueRootCauseDetail;